import { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import { useRequiredContext } from "@redotech/react-util/context";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

export const getTeamUsersQueryKey = () => ["team-users"];

/**
 * The current team's users (id, name, email, roles). Cached by
 * react-query so multiple consumers share a single network request.
 * Stale time is generous since team membership doesn't change often.
 */
export const useTeamUsers = () => {
  const platformRpc = useRequiredContext(RedoPlatformAuthRpcClientContext);
  return useQuery({
    queryKey: getTeamUsersQueryKey(),
    queryFn: () => platformRpc.getTeamUsers(null).then((r) => r.users),
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Map of branded `MongoUserId` (`User_<hex>`) → user name for the
 * current team. Keyed by the prefixed form so consumers can look up
 * directly with the `MongoUserId` returned by effect-api responses
 * without having to strip prefixes themselves. Returns an empty map
 * while the query is loading.
 */
export const useTeamUserNamesById = (): ReadonlyMap<string, string> => {
  const { data } = useTeamUsers();
  return useMemo(
    () =>
      new Map<string, string>(
        (data ?? []).map((u) => [
          MongoUserId.fromString(String(u._id)) as string,
          u.name,
        ]),
      ),
    [data],
  );
};

export { type TeamUser } from "@redotech/redo-merchant-platform-rpc/schema/get-team-users";
